/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useMounted from "@/hooks/mounted";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import { invalidateOccurrenceTypesCatalog } from "@/hooks/occurrenceTypes";
import { IOccurrenceType } from "@/interfaces/occurrenceTypes";
import {
  DeleteResult,
  isDeleteSuccess,
  listIds,
  runDeleteBatch,
} from "@/utils/deleteResults";
import {
  createOccurrenceType,
  deleteOccurrenceType,
  listOccurrenceTypes,
  showOccurrenceType,
  updateOccurrenceType,
} from "@/services";

const DEFAULT_DATA: IOccurrenceType = {
  code: "",
  name: "",
  isActive: true,
};

/** Deriva o code a partir do nome: "Cadastro do cliente" -> "CadastroDoCliente". */
export const codeFromName = (name: string) =>
  (name || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");

export const useOccurrenceTypes = ({
  disabledGetAll,
  disabledShowId,
  defaultLimit,
  filter: filterInitial,
  includes = [],
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>;
  includes?: string[];
} = {}) => {
  const { idOccurrenceType: id } = useParams<{ idOccurrenceType: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [occurrenceTypes, setOccurrenceTypes] = useState<IOccurrenceType[]>([]);
  const [occurrenceType, setOccurrenceType] = useState<IOccurrenceType>(DEFAULT_DATA);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const { filter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit });
  const { goAdd, goEdit, goBack, pathBack } = useEntityNavigation({
    basePath: "/towermanagement/occurrenceTypes",
  });

  const handleOccurrenceType = <K extends keyof IOccurrenceType>(
    key: K,
    value: IOccurrenceType[K],
  ) => {
    setOccurrenceType((current) => ({ ...current, [key]: value }));
  };

  const getAll = ({ page = 1, limit }: { page?: number; limit?: number }) => {
    setLoading(true);
    listOccurrenceTypes({ includes: [...includes], filter, page, limit, sort })
      .then(({ occurrenceTypes: result, limit: limitResult, totalItems }) => {
        setOccurrenceTypes(result);
        setLimit(Number(limitResult));
        setTotalItems(Number(totalItems));
      })
      .finally(() => setLoading(false));
  };

  const search = useEntitySearch({
    list: listOccurrenceTypes,
    key: "occurrenceTypes",
    includes,
    filter,
  });

  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showOccurrenceType({ id })
      .then(({ occurrenceType: result }) => setOccurrenceType(result))
      .finally(() => setLoading(false));
  };

  const create = (onCreate?: (data: IOccurrenceType) => void) => {
    setLoading(true);
    const payload: IOccurrenceType = {
      ...occurrenceType,
      code: occurrenceType.code || codeFromName(occurrenceType.name),
    };
    return createOccurrenceType({ occurrenceType: payload })
      .then(({ occurrenceType: created }) => {
        toast.success("Tipo de ocorrência criado com sucesso!");
        invalidateOccurrenceTypesCatalog();
        setOccurrenceType(created);
        if (onCreate) onCreate(created);
        else navigate(pathBack);
      })
      .finally(() => setLoading(false));
  };

  const update = (onUpdate?: () => void) => {
    if (!occurrenceType?.id) return;
    setLoading(true);
    // code é imutável depois de criado — o DTO de update do backend nem o declara
    const { name, isActive } = occurrenceType;
    return updateOccurrenceType({ id: occurrenceType.id, occurrenceType: { name, isActive } })
      .then(() => {
        toast.success("Tipo de ocorrência atualizado com sucesso!");
        invalidateOccurrenceTypesCatalog();
        if (onUpdate) onUpdate();
        else navigate(pathBack);
      })
      .finally(() => setLoading(false));
  };

  /**
   * Exclui os tipos selecionados na Table. O backend só expõe DELETE /occurrence-types/:id,
   * então vai uma chamada por id, e responde 409 quando o tipo está em uso.
   */
  const deleteMany = async (ids: (string | number)[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const results = await runDeleteBatch(ids, (entityId) =>
        deleteOccurrenceType({ id: entityId }),
      );

      const succeeded = results.filter(isDeleteSuccess);
      const failed = results.filter((result) => !isDeleteSuccess(result));

      if (succeeded.length) {
        toast.success(
          succeeded.length > 1
            ? `${succeeded.length} tipos de ocorrência excluídos com sucesso: ${listIds(succeeded)}.`
            : `Tipo de ocorrência ${listIds(succeeded)} excluído com sucesso!`,
        );
      }

      // Exibe a mensagem do backend, que é específica por vínculo, em vez do resumo genérico do
      // notifyDeleteResults. Agrupa por texto para não repetir a mesma frase uma vez por id.
      const byMessage = new Map<string, DeleteResult[]>();
      failed.forEach((result) => {
        const message = result.data?.message || "Não foi possível excluir.";
        byMessage.set(message, [...(byMessage.get(message) || []), result]);
      });

      byMessage.forEach((items, message) =>
        toast.error(ids.length > 1 ? `${listIds(items)}: ${message}` : message),
      );

      invalidateOccurrenceTypesCatalog();
    } catch {
      // erro sem resposta do servidor (rede/timeout): o interceptor já avisou
    } finally {
      setLoading(false);
      getAll({ page, limit });
    }
  };

  useMounted(() => {
    if (!disabledGetAll) getAll({ page, limit });
  }, [page, limit, filter, sort]);

  // useMounted só dispara a partir da segunda renderização; a carga inicial vem daqui.
  useEffect(() => {
    const isPage = location.pathname.includes("/towermanagement/occurrenceTypes");

    if (id && isPage && !disabledShowId) {
      if (id !== "new") get({ id });
      return;
    }

    if (!disabledGetAll) getAll({ limit });
  }, [id, disabledGetAll, disabledShowId]);

  return {
    occurrenceTypes,
    occurrenceType,
    loading,
    page,
    limit,
    setPage,
    setLimit,
    totalItems,
    id,
    goAdd,
    goEdit,
    goBack,
    handleOccurrenceType,
    handleFilter,
    handleSort,
    create,
    update,
    deleteMany,
    search,
    getAll,
  };
};
